import {
  eccDecryptWithRandom,
  eccEncryptWithRandom,
  exportEccPrivateKey,
  exportEccPublicKey,
  generateEccKeyPair,
  generateEccSigningKeyPair,
  publicKeysMatch as publicKeysMatchForCurve,
} from "../crypto/EccEncryption";
import {
  getEncryptionCurveFromStorage,
  getSigningCurveFromStorage,
} from "./Utility";

/**
 * Resolves the curve for an asymmetric operation.
 *
 * An explicit curve always wins. Otherwise the curve is read from the active session, which
 * throws when absent. There is deliberately no default: silently choosing a curve would run
 * key agreement on the wrong curve for orgs not using the default.
 */
async function resolveEncryptionCurve(eccCurve) {
  return eccCurve || (await getEncryptionCurveFromStorage());
}

async function resolveSigningCurve(eccCurve) {
  return eccCurve || (await getSigningCurveFromStorage());
}

export const encryptAsymmetric = async (
  plainText,
  publicKey,
  associatedData,
  eccCurve
) => {
  return eccEncryptWithRandom(
    plainText,
    publicKey,
    associatedData,
    await resolveEncryptionCurve(eccCurve)
  );
};

export const decryptAsymmetric = async (
  encryptedText,
  privateKey,
  keyEncryptionKey,
  associatedData,
  eccCurve
) => {
  return eccDecryptWithRandom(
    encryptedText,
    privateKey,
    keyEncryptionKey,
    associatedData,
    await resolveEncryptionCurve(eccCurve)
  );
};

export const generateAndExportEccKeyPair = async (eccCurve) => {
  const keyPair = await generateEccKeyPair(await resolveEncryptionCurve(eccCurve));
  return {
    publicKey: await exportEccPublicKey(keyPair.publicKey),
    privateKey: await exportEccPrivateKey(keyPair.privateKey),
  };
};

export const publicKeysMatch = async (
  privateKeyPem,
  candidatePublicKeyPem,
  eccCurve
) => {
  return publicKeysMatchForCurve(
    privateKeyPem,
    candidatePublicKeyPem,
    await resolveEncryptionCurve(eccCurve)
  );
};

export const generateAndExportEccSigningKeyPair = async (eccCurve) => {
  const keyPair = await generateEccSigningKeyPair(
    await resolveSigningCurve(eccCurve)
  );
  return {
    publicKey: await exportEccPublicKey(keyPair.publicKey),
    privateKey: await exportEccPrivateKey(keyPair.privateKey),
  };
};
